import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconDeviceFloppy,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import type {
  PoruchenieRegistryRow,
  PoruchenieRow,
  PoruchenieToken,
} from '@expeditor/shared';
import { poruchenieTemplateApi } from '../../shared/api/poruchenie-template';

/** Локальная редактируемая строка. */
type EditRow =
  | { kind: 'ref'; key: string; enabled: boolean; label: string; showWhenEmpty: boolean }
  | {
      kind: 'custom';
      id: string;
      enabled: boolean;
      label: string;
      value: string;
      showWhenEmpty: boolean;
    };

function newId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `c-${Math.random().toString(36).slice(2)}`;
}

function toEdit(rows: PoruchenieRow[]): EditRow[] {
  return rows.map((r) =>
    r.kind === 'ref'
      ? {
          kind: 'ref',
          key: r.key,
          enabled: r.enabled,
          label: r.label ?? '',
          showWhenEmpty: r.showWhenEmpty ?? true,
        }
      : {
          kind: 'custom',
          id: r.id,
          enabled: r.enabled,
          label: r.label,
          value: r.value,
          showWhenEmpty: r.showWhenEmpty ?? true,
        },
  );
}

function toPayload(rows: EditRow[]): PoruchenieRow[] {
  return rows.map((r) =>
    r.kind === 'ref'
      ? {
          kind: 'ref',
          key: r.key,
          enabled: r.enabled,
          label: r.label.trim() || undefined,
          showWhenEmpty: r.showWhenEmpty,
        }
      : {
          kind: 'custom',
          id: r.id,
          enabled: r.enabled,
          label: r.label,
          value: r.value,
          showWhenEmpty: r.showWhenEmpty,
        },
  );
}

/** Админ: настройка состава таблицы печатной формы «Поручение экспедитору». */
export function PoruchenieTemplatePage() {
  const [rows, setRows] = useState<EditRow[]>([]);
  const [registry, setRegistry] = useState<PoruchenieRegistryRow[]>([]);
  const [tokens, setTokens] = useState<PoruchenieToken[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loaded, setLoaded] = useState(false);

  const defaultLabelByKey = useMemo(
    () => new Map(registry.map((r) => [r.key, r.defaultLabel])),
    [registry],
  );
  const tokenOptions = useMemo(
    () => tokens.map((t) => ({ value: t.token, label: `${t.label} — {{${t.token}}}` })),
    [tokens],
  );

  useEffect(() => {
    poruchenieTemplateApi
      .get()
      .then((dto) => {
        setRows(toEdit(dto.rows));
        setRegistry(dto.registry);
        setTokens(dto.tokens);
        setLoaded(true);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'));
  }, []);

  // Живой предпросмотр: перерисовываем при изменениях (с задержкой).
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => {
      poruchenieTemplateApi
        .preview({ rows: toPayload(rows) })
        .then((r) => setPreviewHtml(r.html))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [rows, loaded]);

  const patch = (i: number, p: Partial<EditRow>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? ({ ...r, ...p } as EditRow) : r)));

  const move = (i: number, dir: -1 | 1) =>
    setRows((rs) => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const next = [...rs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const addCustom = () =>
    setRows((rs) => [
      ...rs,
      { kind: 'custom', id: newId(), enabled: true, label: '', value: '', showWhenEmpty: true },
    ]);

  const remove = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const insertToken = (i: number, token: string) =>
    setRows((rs) =>
      rs.map((r, idx) =>
        idx === i && r.kind === 'custom'
          ? { ...r, value: `${r.value}${r.value && !r.value.endsWith(' ') ? ' ' : ''}{{${token}}}` }
          : r,
      ),
    );

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const dto = await poruchenieTemplateApi.save({ rows: toPayload(rows) });
      setRows(toEdit(dto.rows));
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Печатная форма поручения</Title>
        <Button leftSection={<IconDeviceFloppy size={16} />} onClick={save} loading={busy}>
          Сохранить
        </Button>
      </Group>

      <Text c="dimmed" size="sm">
        Состав и порядок строк основной таблицы бланка «Поручение экспедитору». Снимите галку,
        чтобы скрыть строку; перетаскивайте порядок стрелками. Подпись предустановленной строки
        можно переопределить. Кнопкой ниже добавляются свои строки — в значении можно подставлять
        поля поручения токенами вида <Text span ff="monospace">{'{{cargoVolumeM3}}'}</Text>
        (выберите в списке — вставится в текст).
      </Text>

      {error && <Alert color="red">{error}</Alert>}
      {savedMsg && <Alert color="teal">Сохранено.</Alert>}

      <Grid>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="xs">
            {rows.map((r, i) => (
          <Card key={r.kind === 'ref' ? `ref-${r.key}` : `c-${r.id}`} withBorder padding="sm">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Group gap="xs" align="flex-start" wrap="nowrap" style={{ flex: 1 }}>
                <Stack gap={2}>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <IconArrowUp size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    disabled={i === rows.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <IconArrowDown size={16} />
                  </ActionIcon>
                </Stack>
                <Checkbox
                  mt={4}
                  checked={r.enabled}
                  onChange={(e) => patch(i, { enabled: e.currentTarget.checked })}
                />
                <Stack gap={6} style={{ flex: 1 }}>
                  {r.kind === 'ref' ? (
                    <>
                      <Group gap="xs">
                        <Text fw={500}>{defaultLabelByKey.get(r.key) ?? r.key}</Text>
                        <Badge variant="light" color="gray" size="sm">
                          предустановленная
                        </Badge>
                      </Group>
                      <TextInput
                        size="xs"
                        placeholder={`Своя подпись (по умолчанию: ${
                          defaultLabelByKey.get(r.key) ?? r.key
                        })`}
                        value={r.label}
                        onChange={(e) => patch(i, { label: e.currentTarget.value })}
                      />
                    </>
                  ) : (
                    <>
                      <Group gap="xs">
                        <Badge variant="light" color="blue" size="sm">
                          своя строка
                        </Badge>
                      </Group>
                      <TextInput
                        size="xs"
                        label="Подпись"
                        placeholder="Например: Объём груза"
                        value={r.label}
                        onChange={(e) => patch(i, { label: e.currentTarget.value })}
                      />
                      <Textarea
                        size="xs"
                        label="Значение"
                        placeholder="Текст и/или токены, напр. {{cargoVolumeM3}} м³"
                        autosize
                        minRows={1}
                        value={r.value}
                        onChange={(e) => patch(i, { value: e.currentTarget.value })}
                      />
                      <Select
                        size="xs"
                        placeholder="+ вставить поле (токен)"
                        searchable
                        data={tokenOptions}
                        value={null}
                        onChange={(v) => v && insertToken(i, v)}
                        comboboxProps={{ withinPortal: true }}
                      />
                    </>
                  )}
                  <Checkbox
                    size="xs"
                    label="Выводить, даже если не заполнено"
                    checked={r.showWhenEmpty}
                    onChange={(e) => patch(i, { showWhenEmpty: e.currentTarget.checked })}
                  />
                </Stack>
              </Group>
              {r.kind === 'custom' && (
                <Tooltip label="Удалить строку">
                  <ActionIcon color="red" variant="subtle" onClick={() => remove(i)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Card>
            ))}
          </Stack>
          <Group mt="sm">
            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={addCustom}>
              Добавить свою строку
            </Button>
          </Group>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 5 }}>
          <div style={{ position: 'sticky', top: 16 }}>
            <Text fw={500} size="sm" mb={6}>
              Предпросмотр (примерные данные)
            </Text>
            <iframe
              title="Предпросмотр бланка поручения"
              srcDoc={previewHtml}
              style={{
                width: '100%',
                height: 680,
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 8,
                background: '#fff',
              }}
            />
          </div>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
